import { Driver, Vehicle } from '../Vehicle';
import { VehicleFactory } from './VehicleFactory';
import { Level, LevelName } from './levels/level';
import { PlayerController } from '../PlayerController';
import { AIController, ObjectClassification } from '../AIController';
import { LaneItem, LevelManager } from './LevelManager';
import { RunSettingsHandler } from './RunSettingsHandler';
import { CharacterData, Character, CharacterUpgradesValues, CharacterUpgrades } from '../Character';
import { MathExtras } from './../core/utils/MathExtras';
import OnboardingControls from './../prefabs/OnboardingControls';
// import { StealManager } from './StealManager';
import { GameDataHandler } from '../GameDataHandler';
import { TaxiSkinsPool, VehicleSkinsValues } from '../CharacterSpine';

export class VehicleManager {
  protected scene: Phaser.Scene;
  protected levelManager: LevelManager;
  // protected stealManager: StealManager;

  protected playerVehicleController: PlayerController | undefined;
  protected npcVehicleControllers: AIController[];
  protected playerTaxi: Vehicle | undefined;
  protected vehicles: Vehicle[];
  protected taxis: Vehicle[];
  protected npcTaxis: Vehicle[];
  protected trafficVehicles: Vehicle[];

  // protected laneReservations: Map<number, Phaser.Geom.Rectangle[]>;

  constructor(scene: Phaser.Scene, levelManager: LevelManager) {
    this.scene = scene;
    this.levelManager = levelManager;
    // this.stealManager = new StealManager(this.scene, this);

    this.npcVehicleControllers = [];
    this.vehicles = [];
    this.taxis = [];
    this.npcTaxis = [];
    this.trafficVehicles = [];

    // this.laneReservations = new Map();
  }

  get PlayerTaxi(): Vehicle | undefined {
    return this.playerTaxi;
  }

  get PlayerPosition(): { lane: number; x: number } | undefined {
    if (!this.playerTaxi) return;

    return { lane: this.playerTaxi.CurrentLane, x: this.playerTaxi.x };
  }

  // get StealManager(): StealManager {
  //   return this.stealManager;
  // }

  public init(vehicleFactory: VehicleFactory, level: Level) {
    this.setupTaxis(vehicleFactory, level);
  }

  public start() {
    for (const taxi of this.taxis) {
      taxi.startMoving();
    }
  }

  public pause() {
    for (const taxi of this.taxis) {
      taxi.stopMoving();
    }
  }

  public resume() {
    for (const taxi of this.taxis) {
      taxi.startMoving();
    }
  }

  public end() {
    for (const vehicle of this.vehicles) {
      vehicle.runFinished();
    }
  }

  public registerVehicle(vehicle: Vehicle) {
    if (vehicle.DriverProfile.driver == Driver.PlayerTaxi) {
      this.taxis.push(vehicle);
    } else if (vehicle.DriverProfile.driver == Driver.NPCTaxi) {
      this.npcTaxis.push(vehicle);
      this.taxis.push(vehicle);
    } else if (vehicle.DriverProfile.driver == Driver.Civilian) {
      this.trafficVehicles.push(vehicle);
    }

    this.vehicles.push(vehicle);
  }

  public findNearestVehicleToPoint(frontPointX: number, backPointX: number, vehicleToExclude: Vehicle): Map<number, LaneItem | undefined> {
    let nextVehicle: Map<number, LaneItem | undefined> = new Map();

    for (const vehicle of this.vehicles) {
      if (vehicle == vehicleToExclude) continue;

      let laneItem = nextVehicle.get(vehicle.CurrentLane);
      let bounds = (vehicle.body as MatterJS.BodyType).bounds;

      // Is in front of vehicle?
      if (bounds.min.x > frontPointX) {
        if (!laneItem || bounds.min.x < laneItem.distance) {
          if (!laneItem) laneItem = { classification: ObjectClassification.Vehicle, distance: 0 };
          laneItem.classification = ObjectClassification.Vehicle;
          laneItem.distance = (vehicle.body as MatterJS.BodyType).bounds.min.x;
          nextVehicle.set(vehicle.CurrentLane, laneItem);
        }
        // Is inside of vehicle?
      } else if (MathExtras.getOverlap(backPointX, frontPointX, bounds.min.x, bounds.max.x)) {
        if (!laneItem) laneItem = { classification: ObjectClassification.Vehicle, distance: 0 };
        laneItem.classification = ObjectClassification.Vehicle;
        laneItem.distance = frontPointX;
        nextVehicle.set(vehicle.CurrentLane, laneItem);
      }
    }

    return nextVehicle;
  }

  public findClosestPassengerInVehicle(vehicle: Vehicle): { closestVehicle: Vehicle; distanceBetween: number } {
    let distanceBetween = -1;
    let closestVehicle = vehicle;

    for (const taxi of this.taxis) {
      if (taxi == vehicle) continue;

      if (taxi.HasPassengers) {
        let distance = Phaser.Math.Distance.Between(taxi.x, taxi.y, vehicle.x, vehicle.y);
        if (distanceBetween == -1 || distance < distanceBetween) {
          distanceBetween = distance;
          closestVehicle = taxi;
        }
      }
    }

    return { closestVehicle: closestVehicle, distanceBetween: distanceBetween };
  }

  protected setupTaxis(vehicleFactory: VehicleFactory, level: Level) {
    let playerLane = 0;
    let playerIndentX = 0;
    let playerCharacter = RunSettingsHandler.getSelectedCharacter(this.scene);
    let playerSkin = Character.taxiSkinLookup[playerCharacter];
    let characterData = GameDataHandler.getCharacterData(this.scene, playerCharacter);
    let totalUpgradePoints = characterData.accelerationUpgrades + characterData.topSpeedUpgrades;
    this.setupPlayer(vehicleFactory, playerSkin, characterData, playerLane, playerIndentX);

    // Show onboarding if level1.
    if (level.id == LevelName.Level1 && this.playerVehicleController && this.playerTaxi) {
      let onboardingControls = new OnboardingControls(this.scene, this.PlayerTaxi?.x, this.playerTaxi?.y);
      this.scene.add.existing(onboardingControls);
      onboardingControls.setTarget(this.playerVehicleController, this.playerTaxi);
      onboardingControls.start();
    }

    let possibleSkins: VehicleSkinsValues[] = TaxiSkinsPool.slice();
    let index = possibleSkins.indexOf(playerSkin);
    if (index >= 0) possibleSkins.splice(index, 1);

    for (let i = 0; i < level.taxiDetails.npcTaxis; i++) {
      let defaults = level.taxiDetails.taxis[i];
      let rand = Phaser.Math.Between(0, possibleSkins.length - 1);
      let character = possibleSkins.splice(rand, 1)[0];
      let characterData = this.generateCharacterData(Character.upgradePool.slice(), totalUpgradePoints);
      this.addAI(vehicleFactory, character, characterData, playerLane, defaults?.startLane ?? i + 1, defaults?.startIndentX ?? 1000);
    }
  }

  protected setupPlayer(vehicleFactory: VehicleFactory, playerSkin: VehicleSkinsValues, characterData: CharacterData, lane: number, indentX: number) {
    this.playerTaxi = vehicleFactory.makeVehicle(vehicleFactory.makePlayerProfile(playerSkin, characterData), lane, indentX);
    console.log(
      `Player Stats - Top Speed: ${characterData.topSpeedUpgrades}: ${this.playerTaxi.DriverProfile.topSpeedPerMS} Acceleration: ${characterData.accelerationUpgrades}: ${this.playerTaxi.DriverProfile.accelerationPerMS} MaxSpeed: ${this.playerTaxi.DriverProfile.maxSpeedPerMS}`
    );
    this.playerVehicleController = new PlayerController(this.scene, this.playerTaxi);
  }

  protected addAI(
    vehicleFactory: VehicleFactory,
    playerSkin: VehicleSkinsValues,
    characterData: CharacterData,
    playerStartLane: number,
    startLane: number,
    startDistance: number
  ) {
    let npcTaxi = vehicleFactory.makeVehicle(vehicleFactory.makeAITaxiProfile(playerSkin, characterData), startLane, startDistance);
    console.log(
      `AI Stats - Top Speed: ${characterData.topSpeedUpgrades}: ${npcTaxi.DriverProfile.topSpeedPerMS} Acceleration: ${characterData.accelerationUpgrades}: ${npcTaxi.DriverProfile.accelerationPerMS} MaxSpeed: ${npcTaxi.DriverProfile.maxSpeedPerMS}`
    );
    let aiController = new AIController(this.scene, npcTaxi, this, this.levelManager);
    this.npcVehicleControllers.push(aiController);
  }

  protected generateCharacterData(upgrades: CharacterUpgradesValues[], upgradePoints: number): CharacterData {
    let characterData: CharacterData = {
      topSpeedUpgrades: 0,
      accelerationUpgrades: 0
    };

    for (let i = 0; i < upgradePoints; i++) {
      let randIndex = Phaser.Math.Between(0, upgrades.length - 1);
      let upgrade = upgrades.splice(randIndex, 1)[0];

      switch (upgrade) {
        case CharacterUpgrades.TopSpeed:
          characterData.topSpeedUpgrades++;
          break;
        case CharacterUpgrades.Acceleration:
          characterData.accelerationUpgrades++;
          break;
        default:
          break;
      }
    }

    return characterData;
  }
}
